import React from "react";
import { Controller } from "react-hook-form";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import {
  FormMoneyInput,
  FormPercentageInput,
  InputDate,
} from "../../../../components/Input";
import { SegmentedControl } from "../../../../components/MobileForm";
import { RATE_TYPE_OPTIONS, RATE_TYPE_SEGMENTS } from "../../constants";
import { ResetFrequencySelect } from "./ResetFrequencySelect";
import { PAYMENT_AMOUNT_NOTE, RATE_APPLIES_NOTE } from "./constants";
import { SetInterestRateFieldsProps } from "./types";
import {
  Field,
  FieldLabel,
  FormCard,
  LabelRow,
} from "../../../../components/FormPanel";
import { PanelNote, ResponsiveRow } from "../styles";
import {
  CurrentRateBanner,
  CurrentRateLabel,
  CurrentRateValue,
} from "./styles";

export const SetInterestRateFields: React.FC<SetInterestRateFieldsProps> = ({
  control,
  isSubmitting,
  isVariableRate,
  isCompact,
  currentRateLabel,
}) => {
  return (
    <>
      <CurrentRateBanner>
        <CurrentRateLabel>Current Rate</CurrentRateLabel>
        <CurrentRateValue>{currentRateLabel}</CurrentRateValue>
      </CurrentRateBanner>

      <FormCard>
        <Field>
          <LabelRow>
            <FieldLabel>Rate Type *</FieldLabel>
          </LabelRow>
          <Controller
            name="rateType"
            control={control}
            rules={{ required: "Rate Type is required!" }}
            render={({ field }) => (
              <SegmentedControl
                options={RATE_TYPE_SEGMENTS}
                selectedValue={field.value?.label ?? ""}
                disabled={isSubmitting}
                onChange={(value) => {
                  const option = RATE_TYPE_OPTIONS.find(
                    (opt) => opt.label === value,
                  );
                  field.onChange(option);
                }}
              />
            )}
          />
        </Field>

        <ResponsiveRow>
          <Field>
            <LabelRow>
              <FieldLabel htmlFor="rate-new-rate">New Rate (%) *</FieldLabel>
            </LabelRow>
            <FormPercentageInput
              id="rate-new-rate"
              required
              rules={{ required: "New Rate is required!" }}
              name="interestRate"
              control={control}
              placeholder="0.000 %"
              handleClear={true}
              disabled={isSubmitting}
            />
          </Field>
          <Field>
            <LabelRow>
              <FieldLabel htmlFor="rate-effective-date">
                Effective From *
              </FieldLabel>
            </LabelRow>
            <InputDate
              id="rate-effective-date"
              rules={{ required: "Effective date is required!" }}
              name="effectiveDate"
              placeholder="YYYY/MM/DD"
              control={control}
              disabled={isSubmitting}
            />
          </Field>
        </ResponsiveRow>

        {isVariableRate ? (
          <Field>
            <LabelRow>
              <FieldLabel htmlFor="reset-frequency-select">
                Reset Frequency *
              </FieldLabel>
            </LabelRow>
            <ResetFrequencySelect
              isCompact={isCompact}
              control={control}
              disabled={isSubmitting}
            />
          </Field>
        ) : null}

        <PanelNote $tone="warning">
          <InfoOutlinedIcon />
          {RATE_APPLIES_NOTE}
        </PanelNote>
      </FormCard>

      <FormCard>
        <Field>
          <LabelRow>
            <FieldLabel htmlFor="rate-payment-amount">
              Payment Amount (€)
            </FieldLabel>
          </LabelRow>
          <FormMoneyInput
            id="rate-payment-amount"
            name="paymentAmount"
            placeholder="Define payment amount"
            control={control}
            handleClear={true}
            disabled={isSubmitting}
          />
        </Field>

        <PanelNote $tone="muted">
          <InfoOutlinedIcon />
          {PAYMENT_AMOUNT_NOTE}
        </PanelNote>
      </FormCard>
    </>
  );
};
